import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, defer, EMPTY, expand, filter, finalize, map, mapTo, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { AmlScanEntity, AmlScanQueryParams, PaginationResponse, ReportData, ReportEntity } from '../../api/api.model';
import { ApiService } from '../../api/api.service';

function createEmptyPaginationResponse<T>(): PaginationResponse<T> {
  return {
    content: [],
    empty: false,
    first: false,
    last: false,
    number: 0,
    numberOfElements: 0,
    pageable: {
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      sort: { empty: false, sorted: false, unsorted: false },
      unpaged: false,
    },
    size: 0,
    totalElements: 0,
    totalPages: 0,
  };
}

@Injectable({ providedIn: 'root' })
export class MonitoringFacade {
  private readonly _apiService: ApiService = inject(ApiService);
  private readonly _router: Router = inject(Router);
  private readonly _leaveMonitoring$: Subject<void> = new Subject<void>();
  private readonly _isMonitoringPage: WritableSignal<boolean> = signal(false);

  private readonly _transactionsParams: WritableSignal<Partial<AmlScanQueryParams>> = signal({
    page: 0,
    size: 20,
  });
  private readonly _transactionsRefresh$: Subject<void> = new Subject<void>();

  private readonly _reportsParams: WritableSignal<Partial<AmlScanQueryParams>> = signal({
    page: 0,
    size: 20,
  });
  private readonly _reportsRefresh$: Subject<void> = new Subject<void>();

  private readonly _activeTab: WritableSignal<'transactions' | 'reports'> = signal<'transactions' | 'reports'>('transactions');

  readonly transactionsLoading: WritableSignal<boolean> = signal(false);
  readonly transactionsError: WritableSignal<unknown | null> = signal(null);
  readonly transactionsPagination: WritableSignal<PaginationResponse<AmlScanEntity>> =
    signal(createEmptyPaginationResponse<AmlScanEntity>());
  readonly transactionsUpdatedTick: WritableSignal<number> = signal(0);

  readonly reportsLoading: WritableSignal<boolean> = signal(false);
  readonly reportsError: WritableSignal<unknown | null> = signal(null);
  readonly reportsPagination: WritableSignal<PaginationResponse<Omit<ReportEntity, 'data'>>> =
    signal(createEmptyPaginationResponse<Omit<ReportEntity, 'data'>>());
  readonly reportsUpdatedTick: WritableSignal<number> = signal(0);

  readonly reportsNicknames: WritableSignal<string[]> = signal([]);

  readonly openedReportLoading: WritableSignal<boolean> = signal(false);
  readonly openedReportError: WritableSignal<unknown | null> = signal(null);
  readonly openedReport: WritableSignal<ReportEntity<ReportData> | null> = signal(null);
  readonly sourceTypeForTransactions: WritableSignal<string | null> = signal(null);

  /** Актуальные query-параметры списка транзакций после `_mergeTransactionsParams`. */
  readonly mergedTransactionsParams = computed(() => this._transactionsParams());

  private _mergeTransactionsParams(
    patch: Partial<AmlScanQueryParams>,
    isReset: boolean = false
  ): Partial<AmlScanQueryParams> {
    const hasAddress = Object.prototype.hasOwnProperty.call(patch, 'address');
    const hasFrom = Object.prototype.hasOwnProperty.call(patch, 'from');
    const hasTo = Object.prototype.hasOwnProperty.call(patch, 'to');
    const normalizedPatch: Partial<AmlScanQueryParams> = Object.fromEntries(
      Object.entries(patch).filter(([, v]) => v !== undefined)
    ) as Partial<AmlScanQueryParams>;

    const current = this._transactionsParams();
    const next: Partial<AmlScanQueryParams> = {
      ...current,
      ...normalizedPatch,
    };

    if (patch.sourceType || patch.sourceType === null) {
      this.sourceTypeForTransactions.set(patch.sourceType);
    }

    if (normalizedPatch.riskScore) {
      if ( next.sourceType === 'CLUSTER') {
        delete next.sourceType;
      }
      delete next.incidentProgressStep;
      if (this.sourceTypeForTransactions() && this.sourceTypeForTransactions() !== null) {
        next.sourceType = this.sourceTypeForTransactions() as string;
      }
    }
    if (normalizedPatch.incidentProgressStep) {
      delete next.riskScore;
      if (!normalizedPatch.sourceType) {
        delete next.sourceType;
      }
    }
    if ((hasAddress && patch.address === undefined) || normalizedPatch.address === '') {
      delete next.address;
    }
    if (hasFrom && (patch.from === undefined || patch.from === null || patch.from === '')) {
      delete next.from;
    }
    if (hasTo && (patch.to === undefined || patch.to === null || patch.to === '')) {
      delete next.to;
    }
    if (!patch.sourceType && next.incidentProgressStep?.includes('CASE_REPORTED')) {
      next.sourceType = 'CLUSTER';
    }
    if (next.incidentProgressStep?.includes('RECOVERY_OF_FUNDS')) {
      next.incidentProgressStep = ['RECOVERY_OF_FUNDS', 'CASE_CLOSED'];
    }

    if (patch.incidentProgressStep) {
      next.sourceType = 'CLUSTER';
    }

    if (isReset) {
      delete next.riskScore;
      if (next.incidentProgressStep && next.incidentProgressStep.length > 0 && next.sourceType === 'CLUSTER') {
        delete next.sourceType;
      }
      delete next.incidentProgressStep;
      if (this.sourceTypeForTransactions() && this.sourceTypeForTransactions() !== null) {
        next.sourceType = this.sourceTypeForTransactions() as string;
      }
    }
    return next;
  }

  private _mergeReportsParams(
    patch: Partial<AmlScanQueryParams>
  ): Partial<AmlScanQueryParams> {
    const hasAddress = Object.prototype.hasOwnProperty.call(patch, 'address');
    const hasFrom = Object.prototype.hasOwnProperty.call(patch, 'from');
    const hasTo = Object.prototype.hasOwnProperty.call(patch, 'to');
    const normalizedPatch: Partial<AmlScanQueryParams> = Object.fromEntries(
      Object.entries(patch).filter(([, v]) => v !== undefined)
    ) as Partial<AmlScanQueryParams>;

    const current = this._reportsParams();
    const next: Partial<AmlScanQueryParams> = {
      ...current,
      ...normalizedPatch,
    };
    if ((hasAddress && patch.address === undefined) || normalizedPatch.address === '') {
      delete next.address;
    }
    if ((hasFrom && patch.from === undefined) || normalizedPatch.from === '') {
      delete next.from;
    }
    if ((hasTo && patch.to === undefined) || normalizedPatch.to === '') {
      delete next.to;
    }
    if (next.nickname === '') {
      delete next.nickname;
    }

    return next;
  }

  private _resolveActiveTab(url: string): 'transactions' | 'reports' {
    try {
      const parsed = new URL(url, window.location.origin);
      const tabIndex = parsed.searchParams.get('tabIndex');
      return tabIndex === '1' ? 'reports' : 'transactions';
    } catch {
      return url.includes('tabIndex=1') ? 'reports' : 'transactions';
    }
  }

  private _isMonitoringRoute(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.includes('/monitoring');
    } catch {
      return url.includes('/monitoring');
    }
  }

  private _resetFiltersOnRouteChange(): void {
    this._transactionsParams.set({ page: 0, size: 20 });
    this._reportsParams.set({ page: 0, size: 20 });
    this.sourceTypeForTransactions.set(null);
  }

  private _setActiveTab(tab: 'transactions' | 'reports'): void {
    if (this._activeTab() === tab) return;
    this._activeTab.set(tab);

    // Reset filters on tab switch: keep only shared params
    // (address, from, to) + base pagination.
    const keepCommon = (params: Partial<AmlScanQueryParams>): Partial<AmlScanQueryParams> => {
      const base: Partial<AmlScanQueryParams> = {
        page: 0,
        size: params.size ?? 20,
      };

      if (typeof params.address === 'string' && params.address.length) {
        base.address = params.address;
      }
      if (typeof params.from === 'string' && params.from.length) {
        base.from = params.from;
      }
      if (typeof params.to === 'string' && params.to.length) {
        base.to = params.to;
      }

      return base;
    };

    this._transactionsParams.set(keepCommon(this._transactionsParams()));
    this._reportsParams.set(keepCommon(this._reportsParams()));

    // stop opened report polling when leaving reports
    if (tab !== 'reports') {
      this.closeReport();
    }

    // reset loading flags so UI doesn't show stale spinners on inactive tab
    if (tab !== 'transactions') {
      this.transactionsLoading.set(false);
    }
    if (tab !== 'reports') {
      this.reportsLoading.set(false);
    }

    // start polling for the newly active tab immediately
    if (tab === 'transactions') {
      this._transactionsRefresh$.next();
    } else {
      this._reportsRefresh$.next();
    }
  }

  constructor() {
    // Track active tab from URL. Only the active tab is allowed to poll / request.
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this._router.url)
      )
      .subscribe((url) => {
        const wasMonitoringPage = this._isMonitoringPage();
        const isMonitoringPage = this._isMonitoringRoute(url);
        this._isMonitoringPage.set(isMonitoringPage);

        if (!isMonitoringPage) {
          this._leaveMonitoring$.next();
          this.closeReport();
          this._resetFiltersOnRouteChange();
          this.transactionsLoading.set(false);
          this.reportsLoading.set(false);
          this.openedReportLoading.set(false);
          return;
        }

        const tab = this._resolveActiveTab(url);
        this._setActiveTab(tab);

        // If we just entered monitoring and tab hasn't changed, trigger polling explicitly.
        if (!wasMonitoringPage) {
          if (tab === 'transactions') {
            this._transactionsRefresh$.next();
          } else {
            this._reportsRefresh$.next();
          }
        }
      });

    // Initial tab on service creation
    const initialUrl = this._router.url;
    const isInitialMonitoringPage = this._isMonitoringRoute(initialUrl);
    this._isMonitoringPage.set(isInitialMonitoringPage);
    if (isInitialMonitoringPage) {
      this._setActiveTab(this._resolveActiveTab(initialUrl));
    }

    const fetchTransactionsOnce$ = () =>
      defer(() => {
        this.transactionsLoading.set(true);
        this.transactionsError.set(null);

        const { address, ...transactionsParams } = this._transactionsParams();
        const requestParams: Partial<AmlScanQueryParams> = address
          ? { ...transactionsParams, searchBy: address }
          : transactionsParams;

        return this._apiService.getAmlScanForUser(requestParams).pipe(
          take(1),
          switchMap((data) => {
            this.transactionsPagination.set(data);
            this.transactionsUpdatedTick.update((v) => v + 1);
            return timer(10000).pipe(mapTo(void 0));
          }),
          catchError((err) => {
            this.transactionsError.set(err);
            return timer(10000).pipe(mapTo(void 0));
          }),
          finalize(() => this.transactionsLoading.set(false))
        );
      });

    this._transactionsRefresh$
      .pipe(
        switchMap(() => (this._activeTab() === 'transactions' && this._isMonitoringPage()
          ? fetchTransactionsOnce$().pipe(
            expand(() => fetchTransactionsOnce$()),
            takeUntil(this._leaveMonitoring$)
          )
          : EMPTY))
      )
      .subscribe();

    const fetchReportsOnce$ = () =>
      defer(() => {
        this.reportsLoading.set(true);
        this.reportsError.set(null);
        return this._apiService.getReports(this._reportsParams()).pipe(
          take(1),
          switchMap((data) => {
            this.reportsPagination.set(data);
            this.reportsUpdatedTick.update((v) => v + 1);
            return timer(10000).pipe(mapTo(void 0));
          }),
          catchError((err) => {
            this.reportsError.set(err);
            return timer(10000).pipe(mapTo(void 0));
          }),
          finalize(() => this.reportsLoading.set(false))
        );
      });

    this._reportsRefresh$
      .pipe(
        switchMap(() => (this._activeTab() === 'reports' && this._isMonitoringPage()
          ? fetchReportsOnce$().pipe(
            expand(() => fetchReportsOnce$()),
            takeUntil(this._leaveMonitoring$)
          )
          : EMPTY))
      )
      .subscribe();

    // kick off polling only for active tab
    if (this._isMonitoringPage()) {
      if (this._activeTab() === 'transactions') {
        this._transactionsRefresh$.next();
      } else {
        this._reportsRefresh$.next();
      }
    }
  }

  loadTransactions(params: Partial<AmlScanQueryParams>, isReset: boolean = false): void {
    this._transactionsParams.set(this._mergeTransactionsParams(params, isReset));
    // Only when transactions tab is active
    if (this._activeTab() === 'transactions') {
      this._transactionsRefresh$.next();
    }
  }

  loadReports(params: Partial<AmlScanQueryParams>): void {
    this._reportsParams.set(this._mergeReportsParams(params));
    // Only when reports tab is active
    if (this._activeTab() === 'reports') {
      this._reportsRefresh$.next();
    }
  }

  /**
   * Apply filters shared between transactions/reports (date range, address/search, etc).
   * Saves them for both tabs, but triggers request only for the active tab.
   */
  applyCommonParams(params: Partial<AmlScanQueryParams>): void {
    this._transactionsParams.set(this._mergeTransactionsParams(params));
    this._reportsParams.set(this._mergeReportsParams(params));
    if (this._activeTab() === 'transactions') {
      this._transactionsRefresh$.next();
    } else {
      this._reportsRefresh$.next();
    }
  }

  ensureReportsNicknamesLoaded(): void {
    if (this.reportsNicknames().length) return;
    if (this._activeTab() !== 'reports') return;
    this._apiService.getWalletNicknames().pipe(take(1)).subscribe({
      next: (value) => this.reportsNicknames.set(value),
    });
  }

  openReport(id: number): void {
    if (this._activeTab() !== 'reports') return;
    this.openedReportLoading.set(true);
    this.openedReportError.set(null);
    this._apiService.getReportForId(id).pipe(
      filter(Boolean),
      take(1),
      finalize(() => this.openedReportLoading.set(false))
    ).subscribe({
      next: (report) => {
        this.openedReport.set(report);
      },
      error: (err) => {
        this.openedReportError.set(err);
      }
    });
  }

  closeReport(): void {
    this.openedReport.set(null);
    this.openedReportError.set(null);
    this.openedReportLoading.set(false);
  }
}

